import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button.js';
import infoIcon from '../assets/info-icon.svg';

/**
 * The "complete security verification first" panel (issue #222, parts 1 & 2).
 *
 * Servicing screens (Customer Dashboard, Account Holds, Customer Notes, …) are
 * gated server-side by `requireVerification('medium')` and correctly return 403
 * until the customer has passed Medium or High Security. Reached from a
 * pre-verification security screen those screens used to fire their servicing
 * call regardless and dead-end on a raw "Forbidden: check Teller ID and
 * permissions." / "Could not load accounts." banner — with the agent stranded
 * on a page they should not be on yet (the caller has not been authenticated).
 *
 * When a servicing call comes back 403 (the customer is not yet
 * servicing-verified) the screen renders this calm panel INSTEAD of the raw
 * error — it explains what to do and routes the agent back to the security step.
 * This is a UX affordance ONLY: the authoritative enforcement stays the BFF 403,
 * which is never relaxed. It is 403-driven (not derived from client state) so it
 * never fires when the server does not actually refuse the call.
 */
export function ServicingVerificationRequired(): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <main
      className="max-w-[900px] mx-auto px-12 py-12"
      aria-labelledby="servicing-guard-heading"
    >
      <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3">
        <img
          src={infoIcon}
          alt=""
          aria-hidden="true"
          className="w-5 h-5 mt-1 shrink-0"
        />
        <div>
          <h1
            id="servicing-guard-heading"
            className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-3"
          >
            Complete security verification first
          </h1>
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-4">
            You have not yet completed security verification for this customer,
            so their account information cannot be shown. Complete the security
            questions and two-factor authentication, then continue to the
            customer dashboard.
          </p>
          <Button
            type="button"
            onClick={() => navigate('/security/medium')}
            aria-label="Return to security verification"
            className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
          >
            Return to security verification
          </Button>
        </div>
      </div>
    </main>
  );
}
